import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { postAllocation } from "../time-allotment/post-allocation.ts";
import type {
  AllocationPeriodUi,
  EmployeeAllocationRow,
  ProjectWithAllocations,
} from "../../types/time-allocation.ts";
import { isGlobalAllocationType } from "../../utils/allocation-type.ts";

export type PeriodChangeWarning = {
  projectId: string;
  next: AllocationPeriodUi;
  /** True if any member has a prior project allocation (allocationType !== GLOBAL). */
  hasExistingAllocations: boolean;
};

export type ViewAllocationsTarget = {
  projectId: string;
  employeeId: string;
};

export type PendingDiscard =
  | { kind: "toolbar" }
  | { kind: "navigation"; to: string | null };

export type EmployeeAllocationRowDerived = EmployeeAllocationRow & {
  assignedHours: number;
  remainingHours: number;
};

export type ProjectWithAllocationsDerived = Omit<
  ProjectWithAllocations,
  "employees" | "period"
> & {
  period: AllocationPeriodUi;
  apiPeriod: AllocationPeriodUi;
  employees: EmployeeAllocationRowDerived[];
};

function allocationKey(projectId: string, employeeId: string): string {
  return `${projectId}:${employeeId}`;
}

function hasNonGlobalAllocation(project: ProjectWithAllocations): boolean {
  return project.employees.some((e) => !isGlobalAllocationType(e.allocationType));
}

function parseMonthYearFromIso(iso?: string): { month: number; year: number } {
  if (!iso) {
    const n = new Date();
    return { month: n.getMonth() + 1, year: n.getFullYear() };
  }
  const m = /^(\d{4})-(\d{2})/.exec(iso.trim());
  if (!m) {
    const n = new Date();
    return { month: n.getMonth() + 1, year: n.getFullYear() };
  }
  return { month: Number(m[2]), year: Number(m[1]) };
}

function monthYearForMonthlyProject(project: ProjectWithAllocations): {
  month: number;
  year: number;
} {
  const parsed = parseMonthYearFromIso(project.allocationDate);
  if (project.allocationMonth != null) {
    return {
      month: project.allocationMonth,
      year:
        project.allocationYear ??
        parsed.year ??
        new Date().getFullYear(),
    };
  }
  return parsed;
}

export function useTimeAllocationPage(projects: ProjectWithAllocations[]) {
  const [openAccordionItem, setOpenAccordionItem] = useState<string | null>(
    null,
  );
  const [periodByProjectId, setPeriodByProjectId] = useState<
    Record<string, AllocationPeriodUi>
  >({});
  const [assignedHoursByKey, setAssignedHoursByKey] = useState<
    Record<string, number>
  >({});
  const [savedAssignedHoursByKey, setSavedAssignedHoursByKey] = useState<
    Record<string, number>
  >({});
  const [periodChangeWarning, setPeriodChangeWarning] =
    useState<PeriodChangeWarning | null>(null);
  const [customDatePickerProjectId, setCustomDatePickerProjectId] = useState<
    string | null
  >(null);
  const [viewAllocationsEmployee, setViewAllocationsEmployee] =
    useState<ViewAllocationsTarget | null>(null);
  const [pendingDiscard, setPendingDiscard] = useState<PendingDiscard | null>(
    null,
  );
  const [customRangeByProjectId, setCustomRangeByProjectId] = useState<
    Record<string, { startDate: string; endDate: string }>
  >({});

  const periodChangeWarningRef = useRef<PeriodChangeWarning | null>(null);
  periodChangeWarningRef.current = periodChangeWarning;

  const keysForProject = useCallback(
    (projectId: string): string[] => {
      const p = projects.find((x) => x.id === projectId);
      if (!p) return [];
      return p.employees.map((e) => allocationKey(projectId, e.employeeId));
    },
    [projects],
  );

  useEffect(() => {
    setPeriodByProjectId(() => {
      const next: Record<string, AllocationPeriodUi> = {};
      for (const p of projects) {
        next[p.id] = p.period;
      }
      return next;
    });
  }, [projects]);

  const clearDraftHoursForProject = useCallback(
    (projectId: string) => {
      const keys = keysForProject(projectId);
      setAssignedHoursByKey((prev) => {
        const copy = { ...prev };
        for (const k of keys) {
          copy[k] = 0;
        }
        return copy;
      });
      setSavedAssignedHoursByKey((prev) => {
        const copy = { ...prev };
        for (const k of keys) {
          copy[k] = 0;
        }
        return copy;
      });
    },
    [keysForProject],
  );

  const applyPeriodDirect = useCallback(
    (projectId: string, next: AllocationPeriodUi) => {
      setPeriodByProjectId((prev) => ({
        ...prev,
        [projectId]: next,
      }));
      if (next !== "CUSTOM") {
        setCustomRangeByProjectId((prev) => {
          const copy = { ...prev };
          delete copy[projectId];
          return copy;
        });
      }
    },
    [],
  );

  const requestPeriodChange = useCallback(
    (projectId: string, next: AllocationPeriodUi) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;
      const current = periodByProjectId[projectId] ?? project.period;
      if (next === current) return;

      const hasExistingAllocations = hasNonGlobalAllocation(project);
      setPeriodChangeWarning({ projectId, next, hasExistingAllocations });
    },
    [projects, periodByProjectId],
  );

  const confirmPeriodChange = useCallback(() => {
    const w = periodChangeWarningRef.current;
    if (!w) return;
    const { projectId: pid, next, hasExistingAllocations } = w;

    if (hasExistingAllocations) {
      clearDraftHoursForProject(pid);
    }

    if (next === "CUSTOM") {
      setPeriodChangeWarning(null);
      setCustomDatePickerProjectId(pid);
      setOpenAccordionItem((prev) =>
        prev == null || prev === pid ? pid : prev,
      );
      return;
    }

    applyPeriodDirect(pid, next);
    setPeriodChangeWarning(null);
    setOpenAccordionItem((prev) =>
      prev == null || prev === pid ? pid : prev,
    );
  }, [applyPeriodDirect, clearDraftHoursForProject]);

  const confirmCustomPeriodWithDates = useCallback(
    (projectId: string, startDate: string, endDate: string) => {
      setCustomRangeByProjectId((prev) => ({
        ...prev,
        [projectId]: { startDate, endDate },
      }));
      setPeriodByProjectId((prev) => ({ ...prev, [projectId]: "CUSTOM" }));
      setCustomDatePickerProjectId(null);
      setPeriodChangeWarning(null);
      setOpenAccordionItem((prev) =>
        prev == null || prev === projectId ? projectId : prev,
      );
    },
    [],
  );

  const cancelPeriodChange = useCallback(() => {
    setPeriodChangeWarning(null);
  }, []);

  const cancelCustomDatePicker = useCallback(() => {
    setCustomDatePickerProjectId(null);
  }, []);

  const hasUnsavedAssignedForProject = useCallback(
    (projectId: string) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project) return false;
      for (const emp of project.employees) {
        const key = allocationKey(projectId, emp.employeeId);
        const baseline = Math.round(emp.allottedMinutes / 60);
        const cur =
          assignedHoursByKey[key] !== undefined
            ? assignedHoursByKey[key]!
            : baseline;
        const sav =
          savedAssignedHoursByKey[key] !== undefined
            ? savedAssignedHoursByKey[key]!
            : baseline;
        if (cur !== sav) return true;
      }
      return false;
    },
    [assignedHoursByKey, savedAssignedHoursByKey, projects],
  );

  const flushProjectAssignmentKeys = useCallback(
    (projectId: string) => {
      const keys = keysForProject(projectId);
      setAssignedHoursByKey((prev) => {
        const next = { ...prev };
        for (const k of keys) delete next[k];
        return next;
      });
      setSavedAssignedHoursByKey((prev) => {
        const next = { ...prev };
        for (const k of keys) delete next[k];
        return next;
      });
    },
    [keysForProject],
  );

  const submitProjectAllocations = useCallback(
    async (projectId: string) => {
      const project = projects.find((p) => p.id === projectId);
      if (!project) {
        throw new Error("Project not found");
      }
      const period = periodByProjectId[projectId] ?? project.period;
      const range = customRangeByProjectId[projectId];

      if (period === "CUSTOM") {
        if (!range?.startDate || !range?.endDate) {
          throw new Error("Select a custom date range before saving.");
        }
      }

      const allocations: Record<string, number>[] = [];
      for (const emp of project.employees) {
        const key = allocationKey(projectId, emp.employeeId);
        const draft = assignedHoursByKey[key];
        if (draft === undefined) continue;

        const id = String(emp.employeeId);
        const dedicatedMinutes = Math.round(draft * 60);
        const isPreviouslyAllocated = !isGlobalAllocationType(emp.allocationType);
        if (isPreviouslyAllocated) {
          allocations.push({ [id]: dedicatedMinutes });
        } else if (dedicatedMinutes > 0) {
          allocations.push({ [id]: dedicatedMinutes });
        }
      }

      const anyPositive = allocations.some(
        (entry) => Object.values(entry)[0] > 0,
      );
      if (!anyPositive) {
        throw new Error(
          "Enter allocated hours for at least one team member before saving.",
        );
      }

      const base: Record<string, unknown> = {
        projectId: Number(projectId),
        projectName: project.name,
        allocationType: "EMPLOYEE_PROJECT",
        allocations,
      };

      if (period === "CUSTOM" && range) {
        await postAllocation({
          ...base,
          periodType: "ONE_TIME",
          startDate: range.startDate,
          endDate: range.endDate,
        });
      } else if (period === "MONTHLY") {
        const { month, year } = monthYearForMonthlyProject(project);
        await postAllocation({
          ...base,
          periodType: "MONTHLY",
          month,
          year,
        });
      } else if (period === "WEEKLY") {
        await postAllocation({
          ...base,
          periodType: "WEEKLY",
        });
      }

      flushProjectAssignmentKeys(projectId);
    },
    [
      projects,
      periodByProjectId,
      customRangeByProjectId,
      assignedHoursByKey,
      flushProjectAssignmentKeys,
    ],
  );

  const confirmDiscardAssignedForProject = useCallback(
    (projectId: string) => {
      const keys = keysForProject(projectId);
      setSavedAssignedHoursByKey((saved) => {
        setAssignedHoursByKey((draft) => {
          const nextDraft = { ...draft };
          for (const key of keys) {
            if (saved[key] !== undefined) {
              nextDraft[key] = saved[key]!;
            } else {
              delete nextDraft[key];
            }
          }
          return nextDraft;
        });
        return saved;
      });
    },
    [keysForProject],
  );

  const requestToolbarDiscard = useCallback(() => {
    setPendingDiscard({ kind: "toolbar" });
  }, []);

  const handleAccordionOpenChange = useCallback(
    (next: string | null) => {
      const current = openAccordionItem;
      if (
        current != null &&
        next !== current &&
        hasUnsavedAssignedForProject(current)
      ) {
        setPendingDiscard({ kind: "navigation", to: next });
        return;
      }
      setOpenAccordionItem(next);
    },
    [hasUnsavedAssignedForProject, openAccordionItem],
  );

  const confirmPendingDiscard = useCallback(() => {
    if (pendingDiscard == null || openAccordionItem == null) return;
    confirmDiscardAssignedForProject(openAccordionItem);
    if (pendingDiscard.kind === "navigation") {
      setOpenAccordionItem(pendingDiscard.to);
    }
    setPendingDiscard(null);
  }, [confirmDiscardAssignedForProject, openAccordionItem, pendingDiscard]);

  const cancelPendingDiscard = useCallback(() => {
    setPendingDiscard(null);
  }, []);

  const setAssignedHours = useCallback(
    (projectId: string, employeeId: string, hours: number) => {
      const rounded = Math.max(0, Math.round(hours));
      const key = allocationKey(projectId, employeeId);
      setAssignedHoursByKey((prev) => ({ ...prev, [key]: rounded }));
    },
    [],
  );

  const openViewAllocations = useCallback(
    (projectId: string, employeeId: string) => {
      setViewAllocationsEmployee({ projectId, employeeId });
    },
    [],
  );

  const closeViewAllocations = useCallback(() => {
    setViewAllocationsEmployee(null);
  }, []);

  const projectsWithRemaining = useMemo((): ProjectWithAllocationsDerived[] => {
    return projects.map((project) => {
      const apiPeriod = project.period;
      const period = periodByProjectId[project.id] ?? apiPeriod;
      const range = customRangeByProjectId[project.id];
      const customStartDate = range?.startDate ?? project.customStartDate;
      const customEndDate = range?.endDate ?? project.customEndDate;
      const employees: EmployeeAllocationRowDerived[] = project.employees.map(
        (row) => {
          const key = allocationKey(project.id, row.employeeId);
          const draft = assignedHoursByKey[key];
          const baselineHours = Math.round(row.allottedMinutes / 60);
          const assignedHours =
            draft !== undefined ? draft : baselineHours;
          return {
            ...row,
            assignedHours,
            remainingHours: row.apiRemainingHours,
          };
        },
      );
      return {
        ...project,
        period,
        apiPeriod,
        customStartDate,
        customEndDate,
        employees,
      };
    });
  }, [
    projects,
    periodByProjectId,
    customRangeByProjectId,
    assignedHoursByKey,
  ]);

  return {
    openAccordionItem,
    setOpenAccordionItem,
    periodByProjectId,
    assignedHoursByKey,
    periodChangeWarning,
    customDatePickerProjectId,
    viewAllocationsEmployee,
    projectsWithRemaining,
    pendingDiscard,
    requestPeriodChange,
    confirmPeriodChange,
    confirmCustomPeriodWithDates,
    cancelPeriodChange,
    cancelCustomDatePicker,
    setAssignedHours,
    openViewAllocations,
    closeViewAllocations,
    hasUnsavedAssignedForProject,
    submitProjectAllocations,
    requestToolbarDiscard,
    handleAccordionOpenChange,
    confirmPendingDiscard,
    cancelPendingDiscard,
  };
}
